import React from "react";
import EmptyState from '../../CommonComponents/EmptyState';
import { CalendarDays, Package, ReceiptText, UserRound } from "lucide-react";

// Utility function to format currency
const formatCurrency = (amount) => {
    if (isNaN(amount)) return "₱0.00";
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(amount);
};

const formatDateMMDDYYYY = (value) => {
    if (!value) return "-";
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [yyyy, mm, dd] = str.split("-");
        return `${mm}/${dd}/${yyyy}`;
    }
    const parsed = new Date(str);
    if (Number.isNaN(parsed.getTime())) return str;
    return parsed.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });
};

export default function ReportsTable({ sales = [], loading = false, error = null }) {
    return (
        <div className="overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
            <table className="min-w-[1080px] w-full border-collapse lg:min-w-[980px] xl:min-w-0">
                <thead
                    className="border-b"
                    style={{
                        background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                        borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                    }}
                >
                    <tr>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">No.</th>
                        <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Order #</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Date</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Total Sales</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Orders Count</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Branch</th>
                        <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Cashier</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-500">
                                Loading reports...
                            </td>
                        </tr>
                    ) : error ? (
                        <tr>
                            <td colSpan={7} className="px-6 py-12 text-center text-sm font-semibold text-red-600">
                                Error loading data
                            </td>
                        </tr>
                    ) : sales.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="px-6 py-12 text-center">
                                <div className="flex flex-col items-center justify-center">
                                    <EmptyState
                                        icon={Package}
                                        message="No sales found."
                                        size="default"
                                    />
                                </div>
                            </td>
                        </tr>
                    ) : (
                        sales.map((sale, idx) => (
                            <tr
                                key={sale.id || idx}
                                className="border-b border-slate-100 bg-slate-50/85 transition hover:bg-emerald-50/70 even:bg-white last:border-0 sm:bg-transparent sm:hover:bg-slate-50/70 sm:even:bg-transparent"
                            >
                                <td className="px-4 py-3 text-sm font-medium text-slate-600 xl:px-5">{idx + 1}</td>
                                <td className="px-4 py-3 xl:px-5">
                                    <span className="inline-flex max-w-[240px] items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 shadow-sm">
                                        <ReceiptText className="h-3.5 w-3.5 shrink-0" />
                                        <span className="truncate">
                                            {Array.isArray(sale.order_numbers) && sale.order_numbers.length > 0
                                                ? sale.order_numbers.join(", ")
                                                : (sale.order_number || '-')}
                                        </span>
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center xl:px-5">
                                    <span className="inline-flex min-w-[122px] items-center justify-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 whitespace-nowrap">
                                        <CalendarDays className="h-3.5 w-3.5" />
                                        {formatDateMMDDYYYY(sale.date)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center xl:px-5">
                                    <span className="inline-flex min-w-[112px] items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                        {formatCurrency(sale.totalSales)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center xl:px-5">
                                    <span className="inline-flex min-w-[78px] items-center justify-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                                        {sale.ordersCount}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center text-sm text-slate-700 xl:px-5">
                                    {sale.branch_name || '-'}
                                </td>
                                <td className="px-4 py-3 text-center xl:px-5">
                                    <span className="inline-flex max-w-[180px] items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 shadow-sm">
                                        <UserRound className="h-3.5 w-3.5 shrink-0" />
                                        <span className="truncate">{sale.cashier || '-'}</span>
                                    </span>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
